/**
 * MAPTAC manuscript warning thresholds for unreliable drinking-day predictions.
 * Congruent with processing/Machine_Learning/drinking_prediction_reliability.py
 * (UI is the source of truth for defaults and worn-hours framing).
 */

export const MAPTAC_WARNING_THRESHOLDS = {
  deviceWornHoursMin: 18,
  veryNegativeTacHoursMin: 1,
  artifactHoursMin: 1,
  probaUnreliableLow: 0.05,
  probaUnreliableHigh: 0.95
}

export const RELIABILITY_FLAG_COLUMNS = [
  'unreliable_pred_drinking_day',
  'unreliable_pred_non_wear',
  'unreliable_pred_very_negative_tac',
  'unreliable_pred_artifacts',
  'unreliable_pred_proba'
]

function formatHoursLabel(hours) {
  const n = Number(hours)
  if (!Number.isFinite(n)) return String(hours)
  if (n === 1) return '1 hour'
  return `${n} hours`
}

function formatProbaLabel(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value)
  return n.toFixed(2)
}

/**
 * Column descriptions for reliability flags, using the active MAPTAC thresholds.
 * Use these to override static day-feature docs in export UIs.
 */
export function buildReliabilityFlagDescriptions(thresholds = MAPTAC_WARNING_THRESHOLDS) {
  const t = { ...MAPTAC_WARNING_THRESHOLDS, ...thresholds }
  const wornMin = formatHoursLabel(t.deviceWornHoursMin)
  const veryNegMin = formatHoursLabel(t.veryNegativeTacHoursMin)
  const artifactMin = formatHoursLabel(t.artifactHoursMin)
  const low = formatProbaLabel(t.probaUnreliableLow)
  const high = formatProbaLabel(t.probaUnreliableHigh)

  return {
    unreliable_pred_drinking_day:
      'MAPTAC warning: day may have an unreliable drinking-day prediction (1 = yes).',
    unreliable_pred_non_wear:
      `MAPTAC warning: device worn below ${wornMin} (1 = yes).`,
    unreliable_pred_very_negative_tac:
      `MAPTAC warning triggered by ≥${veryNegMin} of very negative TAC (<−15 µg/L) (1 = yes).`,
    unreliable_pred_artifacts:
      `MAPTAC warning triggered by ≥${artifactMin} of artifacts (jumps + plummets) (1 = yes).`,
    unreliable_pred_proba:
      `MAPTAC warning: predicted probability is between ${low} and ${high} (low confidence) (1 = yes).`
  }
}

export function artifactDurationHours(day) {
  return (Number(day?.jump_duration) || 0) + (Number(day?.plummet_duration) || 0)
}

export function isPositiveDrinkingDetection(day, thresholds = MAPTAC_WARNING_THRESHOLDS) {
  if (Number(day?.pred_drinking_day) === 1) {
    return true
  }
  const proba = Number(day?.proba_drinking_day)
  return day?.proba_drinking_day !== undefined
    && day?.proba_drinking_day !== null
    && !Number.isNaN(proba)
    && proba >= thresholds.probaUnreliableHigh
}

export function computeDrinkingPredictionReliability(day, thresholds = MAPTAC_WARNING_THRESHOLDS) {
  const deviceWorn = Number(day?.device_worn_duration) || 0
  const veryNegative = Number(day?.extreme_negative_duration) || 0
  const artifacts = artifactDurationHours(day)
  const proba = Number(day?.proba_drinking_day)
  const hasProba = day?.proba_drinking_day !== undefined && day?.proba_drinking_day !== null && !Number.isNaN(proba)
  const nonWearFlagged = deviceWorn < thresholds.deviceWornHoursMin

  const flags = {
    unreliable_pred_non_wear: nonWearFlagged ? 1 : 0,
    unreliable_pred_very_negative_tac: veryNegative >= thresholds.veryNegativeTacHoursMin ? 1 : 0,
    unreliable_pred_artifacts: artifacts >= thresholds.artifactHoursMin ? 1 : 0,
    unreliable_pred_proba: hasProba
      && proba > thresholds.probaUnreliableLow
      && proba < thresholds.probaUnreliableHigh
      ? 1
      : 0
  }

  const nonWearPredictionUnreliable = nonWearFlagged && !isPositiveDrinkingDetection(day, thresholds) ? 1 : 0
  flags.unreliable_pred_drinking_day = [
    nonWearPredictionUnreliable,
    flags.unreliable_pred_very_negative_tac,
    flags.unreliable_pred_artifacts,
    flags.unreliable_pred_proba
  ].some(v => v === 1) ? 1 : 0
  return flags
}

export function isNonWearPredictionUnreliable(day, thresholds = MAPTAC_WARNING_THRESHOLDS) {
  const deviceWorn = Number(day?.device_worn_duration) || 0
  const nonWearFlagged = deviceWorn < thresholds.deviceWornHoursMin
  return nonWearFlagged && !isPositiveDrinkingDetection(day, thresholds)
}

export function isUnreliablePredictionDay(day, thresholds = MAPTAC_WARNING_THRESHOLDS) {
  return computeDrinkingPredictionReliability(day, thresholds).unreliable_pred_drinking_day === 1
}

/**
 * Human-readable reasons for a drinking-detection warning marker (!).
 * Includes low-confidence probability and each data-quality contributor.
 */
export function getDrinkingPredictionWarningReasons(day, thresholds = MAPTAC_WARNING_THRESHOLDS) {
  const flags = computeDrinkingPredictionReliability(day, thresholds)
  const reasons = []
  if (flags.unreliable_pred_proba === 1) {
    reasons.push('Low-confidence probability (within uncertainty bounds)')
  }
  if (flags.unreliable_pred_non_wear === 1) {
    reasons.push(`Insufficient device wear (below ${formatHoursLabel(thresholds.deviceWornHoursMin)})`)
  }
  if (flags.unreliable_pred_very_negative_tac === 1) {
    reasons.push(`Excessive very negative TAC (≥${formatHoursLabel(thresholds.veryNegativeTacHoursMin)})`)
  }
  if (flags.unreliable_pred_artifacts === 1) {
    reasons.push(`Excessive artifacts (≥${formatHoursLabel(thresholds.artifactHoursMin)} of jumps + plummets)`)
  }
  return reasons
}

/** Count OR-contributors for low-confidence days (matches drinking_day unreliable logic). */
export function countLowConfidenceContributors(days, thresholds = MAPTAC_WARNING_THRESHOLDS) {
  const counts = { lowDeviceWorn: 0, veryNegativeTac: 0, artifacts: 0, uncertainProba: 0 }
  for (const day of days) {
    const flags = computeDrinkingPredictionReliability(day, thresholds)
    if (flags.unreliable_pred_drinking_day !== 1) continue
    if (isNonWearPredictionUnreliable(day, thresholds)) counts.lowDeviceWorn++
    if (flags.unreliable_pred_very_negative_tac) counts.veryNegativeTac++
    if (flags.unreliable_pred_artifacts) counts.artifacts++
    if (flags.unreliable_pred_proba) counts.uncertainProba++
  }
  return counts
}

function getReliabilityFlag(day, key) {
  if (day?.[key] !== undefined && day?.[key] !== null) {
    return Number(day[key]) === 1
  }
  return computeDrinkingPredictionReliability(day)[key] === 1
}

export function hasNonWearWarning(day) {
  return getReliabilityFlag(day, 'unreliable_pred_non_wear')
}

export function hasVeryNegativeTacWarning(day) {
  return getReliabilityFlag(day, 'unreliable_pred_very_negative_tac')
}

export function hasArtifactWarning(day) {
  return getReliabilityFlag(day, 'unreliable_pred_artifacts')
}

export function hasUncertainProbaWarning(day) {
  return getReliabilityFlag(day, 'unreliable_pred_proba')
}

export function countWarningDays(days, flagCheck) {
  const count = days.filter(flagCheck).length
  const total = days.length
  const pct = total ? ((count / total) * 100).toFixed(1) : '0.0'
  return { count, pct, total }
}

export function isPredictedDrinkingDay(day) {
  return Number(day?.pred_drinking_day) === 1
}

export function isPredictedNonDrinkingDay(day) {
  return Number(day?.pred_drinking_day) === 0
}

export function getDrinkingDetectionVerdict(proba, predictionThreshold = null) {
  const p = Number(proba)
  if (proba === undefined || proba === null || Number.isNaN(p)) {
    return { verdict: 'Unsure', barColor: 'bg-amber-500' }
  }
  if (predictionThreshold != null && !Number.isNaN(Number(predictionThreshold))) {
    const isDrinking = p >= Number(predictionThreshold)
    return {
      verdict: isDrinking ? 'Yes' : 'No',
      barColor: isDrinking ? 'bg-purple-600' : 'bg-slate-500'
    }
  }
  if (p <= MAPTAC_WARNING_THRESHOLDS.probaUnreliableLow) {
    return { verdict: 'No', barColor: 'bg-slate-500' }
  }
  if (p >= MAPTAC_WARNING_THRESHOLDS.probaUnreliableHigh) {
    return { verdict: 'Yes', barColor: 'bg-purple-600' }
  }
  return { verdict: 'Unsure', barColor: 'bg-amber-500' }
}

export function getDrinkingDetectionForDay(day, options = {}) {
  const { predictionThreshold = null } = options
  const proba = day?.proba_drinking_day
  const p = Number(proba)
  const percent = Number.isNaN(p) ? 0 : p

  if (day?.pred_drinking_day !== undefined && day?.pred_drinking_day !== null) {
    const isDrinking = Number(day.pred_drinking_day) === 1
    return {
      verdict: isDrinking ? 'Yes' : 'No',
      barColor: isDrinking ? 'bg-purple-600' : 'bg-slate-500',
      percent
    }
  }

  const { verdict, barColor } = getDrinkingDetectionVerdict(proba, predictionThreshold)
  return { verdict, barColor, percent }
}
